import type { Side } from './espn'

/*
 * Who may see which pick, and until when a pick can be changed.
 *
 * Both answers used to be one board-wide moment: the first kickoff shut every
 * game on the board and revealed every board at once. Scott replaced that on
 * 2026-09-17 with two simpler rules.
 *
 *   Every pick is public as soon as it is made. The pool is one family of
 *   sixteen and seeing what your brother took is most of the fun; the problem
 *   was people not playing, never people peeking.
 *
 *   A pick can be changed until ITS OWN game kicks off. Missing Thursday
 *   night costs you Thursday night, not the other nine games - which is what
 *   the fifteen-minute grace period was reaching for, badly, the day before.
 *
 * The hiding rule is kept as a function rather than deleted, because it is a
 * rule about what the SERVER sends. If the family ever wants the reveal back,
 * it is this function and nothing else - and a client that draws only what it
 * is given cannot leak what it was never sent.
 */

export interface PickRow {
  gameId: string
  playerId: string
  side: Side
}

/**
 * What the viewer may see: everything.
 *
 * Deliberately still a filter. Deleting the call and serialising the raw rows
 * would put this decision nowhere, and the next person who wants a reveal
 * would have to work out where it belongs all over again.
 */
export function visiblePicks(rows: readonly PickRow[]): PickRow[] {
  return [...rows]
}

/**
 * Whether this game is shut, on whichever clock the caller passes.
 *
 * The server passes its own `now` and refuses writes with it. The phone passes
 * the device clock, because it has to stop OFFERING a tap the moment a game
 * starts rather than at its next refetch two minutes later - and it errs shut:
 * a fast clock closes a card early, a slow one still meets the server's no.
 */
export function gameClosed(kickoffAt: number, now: number): boolean {
  return now >= kickoffAt
}

/**
 * When a board's first game starts.
 *
 * No longer a deadline - nothing locks board-wide any more - but the screens
 * still name it ("first kickoff 8:15"), so it is derived here rather than in
 * whichever component happened to need it. Null for a board with no games,
 * which is not a moment in 1970.
 */
export function firstKickoffOf(games: readonly { kickoffAt: number }[]): number | null {
  if (games.length === 0) return null
  return games.reduce((min, g) => (g.kickoffAt < min ? g.kickoffAt : min), games[0]!.kickoffAt)
}

/**
 * What This Week may say before kickoff: a count, never a team.
 *
 * "Cal - 10 of 10, locked" is the nagging screen, and it gives away nothing
 * about which teams he took.
 */
export interface PickCount {
  playerId: string
  picked: number
  lockedAt: number | null
}

export function pickCounts(
  rows: readonly PickRow[],
  players: readonly string[],
  locks: Readonly<Record<string, number | null>>,
): PickCount[] {
  return players.map((playerId) => ({
    playerId,
    picked: rows.filter((r) => r.playerId === playerId).length,
    lockedAt: locks[playerId] ?? null,
  }))
}
